import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, Table, Tag, Button, Input, Space, message,
    Typography, Select, Popconfirm
} from 'antd';
import {
    SearchOutlined, CheckCircleOutlined, CloseCircleOutlined,
    ClockCircleOutlined, EyeOutlined, FlagOutlined, DeleteOutlined
} from '@ant-design/icons';
import ambassadorsService from '../../common/services/ambassadors.service';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

const AmbassadorApplicationsPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [applications, setApplications] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
    const [filters, setFilters] = useState({ search: '', status: '' });

    // Fetch applications
    const fetchApplications = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...filters
            };
            Object.keys(params).forEach(key => !params[key] && delete params[key]);

            const response = await ambassadorsService.getApplications(params);
            if (response.success) {
                setApplications(response.data);
                setPagination(prev => ({
                    ...prev,
                    total: response.pagination?.total || 0
                }));
            }
        } catch (error) {
            console.error('Erreur:', error);
            message.error('Erreur lors du chargement des candidatures');
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, filters]);

    useEffect(() => {
        fetchApplications();
    }, [fetchApplications]);

    // Handlers
    const handleSearch = (value) => {
        setFilters(prev => ({ ...prev, search: value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleStatusFilter = (value) => {
        setFilters(prev => ({ ...prev, status: value || '' }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleTableChange = (paginationConfig) => {
        setPagination(prev => ({
            ...prev,
            page: paginationConfig.current,
            limit: paginationConfig.pageSize
        }));
    };

    const handleViewDetail = (record) => {
        navigate(`/dashboard/ambassador-applications/${record._id}`);
    };

    const handleDelete = async (id) => {
        try {
            const response = await ambassadorsService.deleteApplication(id);
            if (response.success) {
                message.success('Candidature supprimee definitivement');
                fetchApplications();
            } else {
                message.error(response.message || 'Erreur lors de la suppression');
            }
        } catch (error) {
            console.error('Erreur:', error);
            message.error('Erreur lors de la suppression');
        }
    };

    // Helper functions
    const getProfileTypeLabel = (type) => {
        const labels = {
            'user_client': 'Utilisateur/Client',
            'professional': 'Professionnel',
            'driver_operator': 'Chauffeur/Operateur',
            'influencer': 'Influenceur',
            'entrepreneur_leader': 'Entrepreneur/Leader',
            'other': 'Autre'
        };
        return labels[type] || type;
    };

    const getAvailabilityLabel = (value) => {
        const labels = {
            'occasional': 'Occasionnel',
            'regular': 'Regulier',
            'very_active': 'Tres actif'
        };
        return labels[value] || value;
    };

    // Columns
    const columns = [
        {
            title: 'Candidat',
            key: 'name',
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 500 }}>{record.firstName} {record.lastName}</div>
                    <div style={{ fontSize: '12px', color: '#888' }}>{record.phone}</div>
                </div>
            )
        },
        {
            title: 'Profil',
            key: 'profile',
            render: (_, record) => (
                <div>
                    <div>{record.profileType?.slice(0, 2).map(p => getProfileTypeLabel(p)).join(', ') || '-'}</div>
                    <div style={{ fontSize: '12px', color: '#888' }}>
                        {record.hasCommunity ? 'A une communaute' : 'Sans communaute'}
                    </div>
                </div>
            )
        },
        {
            title: 'Disponibilite',
            key: 'availability',
            render: (_, record) => (
                <Tag color={record.availability === 'very_active' ? 'green' : record.availability === 'regular' ? 'blue' : 'default'}>
                    {getAvailabilityLabel(record.availability)}
                </Tag>
            )
        },
        {
            title: 'Ville',
            dataIndex: 'city',
            key: 'city',
            render: (text) => text || '-'
        },
        {
            title: 'Statut',
            key: 'status',
            render: (_, record) => {
                const statusConfig = {
                    pending: { color: 'warning', icon: <ClockCircleOutlined />, text: 'En attente' },
                    approved: { color: 'success', icon: <CheckCircleOutlined />, text: 'Approuvee' },
                    rejected: { color: 'error', icon: <CloseCircleOutlined />, text: 'Rejetee' }
                };
                const config = statusConfig[record.status] || statusConfig.pending;
                return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
            }
        },
        {
            title: 'Date',
            key: 'date',
            render: (_, record) => (
                <span style={{ fontSize: '12px' }}>
                    {dayjs(record.createdAt).format('DD/MM/YYYY HH:mm')}
                </span>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDetail(record)}
                    >
                        Detail
                    </Button>
                    <Popconfirm
                        title="Supprimer cette candidature ?"
                        description="Cette action est irreversible. La candidature sera supprimee definitivement."
                        onConfirm={() => handleDelete(record._id)}
                        okText="Supprimer"
                        cancelText="Annuler"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div style={{ padding: '24px' }}>
            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
                <Title level={3} style={{ margin: 0 }}>
                    <FlagOutlined style={{ marginRight: '12px' }} />
                    Candidatures Ambassadeur
                </Title>
            </div>

            {/* Filters */}
            <Card style={{ marginBottom: '24px' }}>
                <Space wrap>
                    <Input.Search
                        placeholder="Rechercher par nom, telephone..."
                        allowClear
                        onSearch={handleSearch}
                        style={{ width: 300 }}
                        prefix={<SearchOutlined />}
                    />
                    <Select
                        placeholder="Statut"
                        style={{ width: 150 }}
                        onChange={handleStatusFilter}
                        value={filters.status || ''}
                        defaultValue=""
                    >
                        <Option value="">Toutes</Option>
                        <Option value="pending">En attente</Option>
                        <Option value="approved">Approuvee</Option>
                        <Option value="rejected">Rejetee</Option>
                    </Select>
                </Space>
            </Card>

            {/* Table */}
            <Card>
                <Table
                    columns={columns}
                    dataSource={applications}
                    rowKey="_id"
                    loading={loading}
                    pagination={{
                        current: pagination.page,
                        pageSize: pagination.limit,
                        total: pagination.total,
                        showSizeChanger: true,
                        showTotal: (total) => `Total: ${total}`
                    }}
                    onChange={handleTableChange}
                />
            </Card>
        </div>
    );
};

export default AmbassadorApplicationsPage;
